import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import { isChipWithinWindow, type TopBarChipConfig } from '@pm/shared';
import { apiClient } from '../../api/client';
import { chipBackground, chipTextColor } from '../../lib/chipColors';

/**
 * SMSPRO-178 (Portierung nach ProjectHQ) — Konfigurierbare Chips in der Kopfzeile.
 *
 * ARBEITSTEILUNG: Die ROLLE prueft das BFF — sie aendert sich waehrend einer
 * Sitzung nicht, und der Abruf liefert nur Chips, die der Nutzer sehen darf.
 * Das ZEITFENSTER wird hier zusaetzlich gegen eine tickende Uhr geprueft, denn
 * es ist der fluechtige Teil: zwischen zwei Abrufen liegt bis zu eine Minute,
 * und ein Chip, der bis 12:00 gilt, darf nicht bis 12:01 stehen bleiben.
 * Beide Seiten nutzen dieselbe Regel aus @pm/shared.
 */

/**
 * Oberflaechen-Kennung, die an /active mitgeschickt wird. Derselbe Chip-Bestand
 * bedient mehrere Oberflaechen — ein Chip fuer SMS PRO darf hier nicht erscheinen.
 * Das BFF normalisiert 'projecthq' -> 'PHQ' (siehe @pm/shared normalizeFrontendCode).
 */
const APP_CODE = 'projecthq';

/** Abstand zwischen zwei Abrufen. Neu angelegte Chips erscheinen so binnen einer Minute. */
const CHIP_POLL_MS = 60_000;

/**
 * Takt der lokalen Neubewertung. Deutlich kuerzer als der Abruf, damit ein
 * ablaufendes Zeitfenster zeitnah greift, ohne Netzlast zu erzeugen.
 */
const CHIP_TICK_MS = 15_000;

export function TopBarChips() {
  const [chips, setChips] = useState<TopBarChipConfig[]>([]);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    let cancelled = false;

    async function fetchChips() {
      try {
        const resp = await apiClient.get<TopBarChipConfig[]>('/api/v1/topbar-chips/active', {
          app: APP_CODE,
        });
        if (!cancelled) setChips(resp.data ?? []);
      } catch {
        // Chips sind schmueckend, kein kritischer Inhalt. Bewusst KEIN Leeren:
        // ein Netzfehler soll einen gueltigen Chip nicht abraeumen.
      }
    }

    fetchChips();
    const poll = window.setInterval(fetchChips, CHIP_POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchChips();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', fetchChips);

    return () => {
      cancelled = true;
      window.clearInterval(poll);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', fetchChips);
    };
  }, []);

  // Eigener Takt fuer die Zeitpruefung — unabhaengig vom Abruf.
  useEffect(() => {
    const tick = window.setInterval(() => setNow(new Date()), CHIP_TICK_MS);
    return () => window.clearInterval(tick);
  }, []);

  const visible = chips.filter((chip) => isChipWithinWindow(chip, now));
  if (visible.length === 0) return null;

  return (
    <Box
      data-testid="topbar-chips"
      sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pointerEvents: 'none' }}
    >
      {visible.map((chip) => {
        const bg = chipBackground(chip.color);
        return (
          <Chip
            key={chip.id}
            label={chip.label}
            size="small"
            sx={{
              bgcolor: bg,
              color: chipTextColor(bg),
              fontWeight: 700,
              letterSpacing: '0.06em',
              px: 1,
              borderRadius: 0.75,
            }}
          />
        );
      })}
    </Box>
  );
}
